import { useState } from 'react';
import { MapPin, Mail, Building2, Send, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        company: '',
        phone: '',
        email: '',
        message: '',
      });
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Failed to submit. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section 
        data-testid="contact-hero"
        className="relative py-20 md:py-32 bg-[#1A2B4A]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="overline mb-4">Contact Us</p>
            <h1 
              data-testid="contact-title"
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
            >
              Let's Discuss Your<br />
              <span className="text-[#C9A84C]">Next Project</span>
            </h1>
            <p className="text-lg text-[#F5F5F0]/90">
              Get in touch with our team to explore how we can support your 
              infrastructure and engineering needs.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section 
        data-testid="contact-form-section"
        className="py-20 md:py-32 bg-[#F5F5F0]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <div className="bg-white p-8 md:p-10 rounded-sm shadow-lg border border-[#4A5568]/10">
              <h2 className="text-2xl font-bold text-[#1A2B4A] mb-2">Send us a Message</h2>
              <p className="text-[#4A5568] mb-8">Fill out the form and our team will get back to you shortly.</p>

              {isSubmitted ? (
                <div 
                  data-testid="contact-success-message"
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A2B4A] mb-2">Message Sent!</h3>
                  <p className="text-[#4A5568]">
                    Thank you for reaching out. We'll get back to you within 24-48 hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="mt-6 text-[#C9A84C] font-semibold hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#1A2B4A] font-medium mb-2 block">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-input-name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="form-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-[#1A2B4A] font-medium mb-2 block">
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="contact-input-company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-[#1A2B4A] font-medium mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        data-testid="contact-input-email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="form-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#1A2B4A] font-medium mb-2 block">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="contact-input-phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#1A2B4A] font-medium mb-2 block">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-input-message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or inquiry..."
                      className="form-input resize-none"
                    />
                  </div>

                  {error && (
                    <div 
                      data-testid="contact-error-message"
                      className="text-red-600 text-sm bg-red-50 p-3 rounded"
                    >
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-[#C9A84C] text-[#1A2B4A] font-bold hover:bg-[#b0923f] py-3 rounded-sm transition-all flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>Sending...</>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Address Card */}
              <div 
                data-testid="contact-address-card"
                className="bg-white p-8 rounded-sm shadow-lg border border-[#4A5568]/10"
              >
                <h3 className="text-xl font-bold text-[#1A2B4A] mb-6 flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-[#C9A84C]" />
                  Registered Office
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#C9A84C] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-[#1A2B4A]">Kamal Engineers and Contractors Pvt Ltd</p>
                      <p className="text-[#4A5568]">
                        Shree Niketan, 380, Ashok Nagar,<br />
                        Udaipur – 313001, Rajasthan, India
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#C9A84C]" />
                    <p className="text-[#4A5568]">info@kec-india.com</p>
                  </div>
                </div>
              </div>

              {/* Legal Info Card */}
              <div 
                data-testid="contact-legal-card"
                className="bg-[#1A2B4A] p-8 rounded-sm"
              >
                <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-wider mb-2">
                  Corporate Identity Number
                </p>
                <p className="text-white text-lg font-mono mb-4">U45201RJ1983PTC002692</p>
                <p className="text-[#F5F5F0]/70 text-sm">
                  Registered under the Companies Act, 1956
                </p>
              </div>

              {/* Map Placeholder */}
              <div 
                data-testid="contact-map-placeholder"
                className="map-placeholder rounded-sm overflow-hidden"
              >
                <div className="text-center p-8">
                  <MapPin className="w-12 h-12 text-[#4A5568]/40 mx-auto mb-4" />
                  <p className="text-[#4A5568] font-medium">Ashok Nagar, Udaipur</p>
                  <p className="text-[#4A5568]/70 text-sm">Rajasthan 313001, India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Hours */}
      <section 
        data-testid="business-hours"
        className="py-16 bg-[#1A2B4A]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Business Hours</h3>
              <p className="text-[#F5F5F0]/70">Monday – Saturday: 9:00 AM – 6:00 PM IST</p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-[#F5F5F0]/70 text-sm mb-2">For urgent inquiries</p>
              <p className="text-[#C9A84C] font-semibold">info@kec-india.com</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
